import { useState, useEffect } from 'react';
import GlassCard from '../../components/GlassCard';
import "../styles/exerciseBlock.css";
import { BodyRegionDisplayMap } from './BodyRegionMap';

const TABS = ['overview', 'video', 'extra'];

export function ExerciseBlock(props) {
  const {
    id,
    name,
    difficulty,
    sets,
    estimatedTimeSec,
    inSession,
    option,
    isExpanded,
    onToggleExpand,
    onCompleteExercise,

    sessionImages = [],
    sessionYoutubeUrl,
    shortDescription,
    sessionMusclesImageUrl,
    timerConfig,

    muscles = [],
    longDescription,
    detailImages = [],
    detailMusclesImageUrl,
    detailYoutubeUrl,
    body_region = [],

    commonMistakes = [],
    l_m = "57px",
    new_exercise = false,

    completed: completedProp = false,
    isBreak = false,
    detailLoading = false,
    onAddToPlan,
  } = props;

  const [completed, setCompleted] = useState(Boolean(completedProp));
  const [checked, setChecked] = useState(false);
  const [addedToPlan, setAddedToPlan] = useState(false);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    setCompleted(Boolean(completedProp));
  }, [completedProp]);

  const isSessionMode = option === "inSession" || inSession === true;
  const isNewExerciseMode = option === "newExercise" || new_exercise === true;
  const isPhysioMode = option === "onPhysio";

  const normalizedMuscles = Array.isArray(muscles) ? muscles : [];
  const normalizedCommonMistakes = Array.isArray(commonMistakes) ? commonMistakes : [];
  const normalizedDetailImages = Array.isArray(detailImages) ? detailImages : [];
  const normalizedBodyRegions = Array.isArray(body_region) ? body_region : [];

  const handleComplete = async (e) => {
    e.stopPropagation();
    if (completed || isBreak) return;

    setCompleted(true);

    try {
      if (onCompleteExercise) {
        await onCompleteExercise(id);
      }
    } catch (err) {
      setCompleted(false);
    }
  };

  const handleChecked = async (e) => {
    e.stopPropagation();
    try {
      if (onCompleteExercise) {
        await onCompleteExercise(id);
      } else {
        setChecked((prev) => !prev);
      }
      onToggleExpand && onToggleExpand(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddToPlan = (e) => {
    e.stopPropagation();

    const payload = {
      id,
      name,
      difficulty,
      muscles: normalizedMuscles,
      longDescription,
      detailImages: normalizedDetailImages,
      detailMusclesImageUrl,
      detailYoutubeUrl,
      body_region: normalizedBodyRegions,
      commonMistakes: normalizedCommonMistakes,
    };

    if (onAddToPlan) {
      onAddToPlan(payload);
    }

    setAddedToPlan((prev) => !prev);
  };

  const handleToggleLike = (e) => {
    e.stopPropagation();
    setLiked((prev) => !prev);
  };

  const handleToggle = () => {
    if (isSessionMode && (completed || isBreak)) return;
    onToggleExpand && onToggleExpand(id);
  };

  return (
    <div className="w-full">
      <GlassCard
        className={[
          "exercise-header",
          isExpanded ? "exercise-header-expanded" : "",
          completed ? "exercise-header-completed" : "",
          isBreak ? "exercise-header-break" : "",
        ].join(" ")}
        onClick={handleToggle}
        style={{
          padding: "10px",
          width: "100%",
          maxWidth: "100%",
          marginLeft: 0,
          boxSizing: "border-box",
          boxShadow: "none",
          opacity: completed ? 0.72 : 1,
        }}
      >
        <div className="header-cell name">{name}</div>
        <div className="header-cell">{difficulty}</div>

        <div className="header-cell">
          {isSessionMode && !isBreak && sets ? `${sets} sets` : ""}
        </div>

        <div className="header-cell">
          {isSessionMode && estimatedTimeSec ? formatTimeLabel(estimatedTimeSec) : ""}
        </div>

        {isSessionMode && (
          <button
            type="button"
            onClick={handleComplete}
            disabled={completed || isBreak}
            className="exercise-complete-btn"
          >
            {isBreak ? "Break" : completed ? "Completed" : "Mark As Done"}
          </button>
        )}

        {isNewExerciseMode && (
          <button
            type="button"
            onClick={handleChecked}
            className="exercise-complete-btn"
          >
            {checked ? "Checked" : "Checked"}
          </button>
        )}

        {isPhysioMode && (
          <div
            className="exercise-physio-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleAddToPlan}
              className="exercise-complete-btn exercise-add-plan-btn"
            >
              {addedToPlan ? "Added" : "Add to plan"}
            </button>

            <button
              type="button"
              onClick={handleToggleLike}
              className={[
                "exercise-heart-btn",
                liked ? "exercise-heart-btn-liked" : "",
              ].join(" ")}
              aria-label={liked ? "Remove from liked" : "Add to liked"}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill={liked ? "currentColor" : "none"}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 21s-6.716-4.35-9.193-8.01C.744 9.96 2.07 5.5 6.09 4.6c2.09-.47 4.16.3 5.41 1.98C12.75 4.9 14.82 4.13 16.91 4.6c4.02.9 5.35 5.36 3.28 8.39C18.72 16.65 12 21 12 21Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </GlassCard>

      {isExpanded && !completed && !isBreak && (
        <div className="mt-2">
          {isSessionMode ? (
            <SessionExpansion
              sessionImages={sessionImages}
              sessionYoutubeUrl={sessionYoutubeUrl}
              shortDescription={shortDescription}
              musclesImageUrl={sessionMusclesImageUrl}
              timerConfig={timerConfig}
              sets={sets}
              completed={completed}
              commonMistakes={normalizedCommonMistakes}
              body_region={normalizedBodyRegions}
              l_m={l_m}
            />
          ) : (
            <DetailsExpansion
              muscles={normalizedMuscles}
              longDescription={longDescription}
              detailImages={normalizedDetailImages}
              detailMusclesImageUrl={detailMusclesImageUrl}
              detailYoutubeUrl={detailYoutubeUrl}
              commonMistakes={normalizedCommonMistakes}
              body_region={normalizedBodyRegions}
              l_m={l_m}
              detailLoading={detailLoading}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SessionExpansion({
  sessionImages = [],
  sessionYoutubeUrl,
  shortDescription,
  musclesImageUrl,
  timerConfig,
  sets = 0,
  completed,
  commonMistakes,
  body_region = [],
  l_m,
}) {
  const [currentSet, setCurrentSet] = useState(1);
  const [phase, setPhase] = useState("work");
  const [remaining, setRemaining] = useState(
    timerConfig ? timerConfig.workSecPerSet : 0
  );
  const [flash, setFlash] = useState(false);
  const [phaseFlash, setPhaseFlash] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const totalSets = timerConfig ? timerConfig.setCount : sets || 0;
  const workSec = timerConfig ? timerConfig.workSecPerSet : 0;
  const restSec = timerConfig ? timerConfig.restSecBetweenSets : 0;
  const normalizedBodyRegions = Array.isArray(body_region) ? body_region : [];

  useEffect(() => {
    if (!timerConfig) return;
    setCurrentSet(1);
    setPhase("work");
    setRemaining(workSec);
    setFlash(false);
    setIsRunning(false);
  }, [workSec, restSec, totalSets, timerConfig]);

  useEffect(() => {
    if (!timerConfig || completed) return;
    if (!isRunning) return;
    if (totalSets === 0) return;

    if (remaining <= 0) {
      if (phase === "work") {
        setPhaseFlash(true);
        setTimeout(() => setPhaseFlash(false), 350);
      }

      if (phase === "work" && restSec > 0) {
        setPhase("rest");
        setRemaining(restSec);
        return;
      }

      if (currentSet < totalSets) {
        setCurrentSet((s) => s + 1);
        setPhase("work");
        setRemaining(workSec);
        return;
      }

      setIsRunning(false);
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      return () => clearTimeout(t);
    }

    const interval = setInterval(() => {
      setRemaining((r) => r - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [remaining, phase, currentSet, totalSets, completed, timerConfig, isRunning, workSec, restSec]);

  const toggleTimer = () => {
    if (!timerConfig || completed) return;

    setIsRunning((running) => {
      if (!running && remaining <= 0) {
        setCurrentSet(1);
        setPhase("work");
        setRemaining(workSec);
      }
      return !running;
    });
  };

  return (
    <div className="relative">
      {flash && <div className="session-flash-overlay" />}
      {phaseFlash && <div className="session-phase-flash-overlay" />}
      {flash && <div className="session-flash-overlay" />}

      <style jsx>{`
        @keyframes flash {
          0% { opacity: 0; }
          30% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>

      <GlassCard
        className="session-card"
        style={{
          padding: "10px",
          maxWidth: "725px",
          marginLeft: l_m,
          boxShadow: "none",
        }}
      >
        <div className="session-left">
          <div className="session-image-row">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="session-image-slot">
                {sessionImages[i] ? (
                  <img
                    src={sessionImages[i]}
                    alt={`Exercise ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  `IMAGE ${i + 1}`
                )}
              </div>
            ))}
          </div>

          {sessionYoutubeUrl && (
            <div className="session-video">
              <iframe
                src={convertToEmbed(sessionYoutubeUrl)}
                className="w-full h-full"
                title="Exercise video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {shortDescription && (
            <div className="session-short-desc">{shortDescription}</div>
          )}

          <div className="common-mistakes-t">Common mistakes:</div>
          {commonMistakes.length === 0 ? (
            <p className="text-sm">No data.</p>
          ) : (
            <ul className="list-disc list-inside text-sm space-y-1">
              {commonMistakes.map((m, idx) => (
                <li key={idx}>{m}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="session-right">
          <div
            onClick={toggleTimer}
            className={
              "session-timer-box " + (!isRunning ? "session-timer-paused" : "")
            }
          >
            <div className="session-timer-phase">
              {phase === "work" ? "Work" : "Rest"} {!isRunning ? "(stopped)" : ""}
            </div>
            <div className="session-timer-time">
              {formatTime(Math.max(remaining, 0))}
            </div>
            <div className="session-timer-meta">
              Set {currentSet}/{totalSets || 1}
            </div>
            <div className="session-timer-meta">
              Tap to {isRunning ? "pause" : "start"}
            </div>
          </div>

          <div className="session-muscles">
            {normalizedBodyRegions.length > 0 ? (
              <BodyRegionDisplayMap activeRegions={normalizedBodyRegions} />
            ) : musclesImageUrl ? (
              <img
                src={musclesImageUrl}
                alt="Muscle groups"
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-[10px]">Muscle map</span>
            )}
          </div>
        </div>
      </GlassCard>

      {totalSets > 0 && (
        <div className="session-sets-row">
          {Array.from({ length: Math.min(totalSets, 10) }).map((_, i) => {
            const index = i + 1;
            const isDone = index < currentSet;
            const isCurrent = index === currentSet && phase === "work";
            const isActive = isDone || isCurrent;
            return (
              <div
                key={index}
                className={
                  "session-set-pill " +
                  (isActive ? "session-set-pill-active" : "")
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function DetailsExpansion({
  muscles = [],
  longDescription,
  detailImages = [],
  detailMusclesImageUrl,
  detailYoutubeUrl,
  commonMistakes = [],
  body_region = [],
  l_m,
  detailLoading = false,
}) {
  const [tab, setTab] = useState("overview");
  const normalizedBodyRegions = Array.isArray(body_region) ? body_region : [];

  if (detailLoading) {
    return (
      <GlassCard
        className="details-card"
        style={{
          padding: "10px",
          maxWidth: "725px",
          marginLeft: l_m,
        }}
      >
        <div className="text-sm" style={{ opacity: 0.75 }}>
          Loading exercise details...
        </div>
      </GlassCard>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <GlassCard
        className="details-card"
        style={{
          padding: "10px",
          maxWidth: "725px",
          marginLeft: l_m,
        }}
      >
        {tab === "overview" && (
          <div className="details-overview">
            <div className="details-left">
              <div className="details-box">
                <div className="details-box-title">Muscles:</div>
                <ul className="details-box-body">
                  {muscles.length === 0 ? (
                    <li>No data.</li>
                  ) : (
                    muscles.map((m) => <li key={m}>{m}</li>)
                  )}
                </ul>
              </div>

              <div className="details-image-row">
                <div className="details-image-slot">
                  <div className="details-box-title">Long description:</div>
                  <p className="text-sm">
                    {longDescription || "No description yet."}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 h-32 bg-slate-300/40 rounded-md overflow-hidden flex items-center justify-center text-xs"
                  >
                    {detailImages[i] ? (
                      <img
                        src={detailImages[i]}
                        alt={`Exercise detail ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      `IMAGE ${i + 1}`
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="details-right">
              {normalizedBodyRegions.length > 0 ? (
                <BodyRegionDisplayMap activeRegions={normalizedBodyRegions} />
              ) : detailMusclesImageUrl ? (
                <img
                  src={detailMusclesImageUrl}
                  alt="Muscle map"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="details-box-title">Muscle map</span>
              )}
            </div>
          </div>
        )}

        {tab === "video" && (
          <div className="details-video">
            {detailYoutubeUrl ? (
              <iframe
                src={convertToEmbed(detailYoutubeUrl)}
                className="w-full h-full"
                title="Exercise detail video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <div className="text-sm">No video yet.</div>
            )}
          </div>
        )}

        {tab === "extra" && (
          <div className="details-overview">
            <div className="border rounded-lg px-3 py-2 bg-white/10">
              <div className="details-box-tit">Common mistakes:</div>
              {commonMistakes.length === 0 ? (
                <p className="text-sm">No data.</p>
              ) : (
                <ul className="list-disc list-inside text-sm space-y-1">
                  {commonMistakes.map((m, idx) => (
                    <li key={idx}>{m}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </GlassCard>

      <div className="details-tabs-controls">
        <button
          type="button"
          className="details-tabs-arrow"
          onClick={() =>
            setTab((prev) => {
              const idx = TABS.indexOf(prev);
              return TABS[(idx - 1 + TABS.length) % TABS.length];
            })
          }
        >
          ‹
        </button>

        {TABS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              "details-tabs-dot " +
              (tab === k ? "details-tabs-dot-active" : "")
            }
          />
        ))}

        <button
          type="button"
          className="details-tabs-arrow"
          onClick={() =>
            setTab((prev) => {
              const idx = TABS.indexOf(prev);
              return TABS[(idx + 1) % TABS.length];
            })
          }
        >
          ›
        </button>
      </div>
    </div>
  );
}

function formatTime(sec) {
  if (!sec && sec !== 0) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatTimeLabel(seconds) {
  if (!seconds || Number(seconds) <= 0) return "-";
  const total = Number(seconds);
  const min = Math.floor(total / 60);
  const sec = total % 60;
  if (min > 0 && sec > 0) return `${min}m ${sec}s`;
  if (min > 0) return `${min}m`;
  return `${sec}s`;
}

function convertToEmbed(url) {
  if (!url) return "";
  if (url.includes("embed")) return url;
  const fromV = url.match(/v=([^&]+)/);
  const fromShort = url.match(/youtu\.be\/([^?]+)/);
  const id = (fromV && fromV[1]) || (fromShort && fromShort[1]) || "";
  return id ? `https://www.youtube.com/embed/${id}` : url;
}